import { CloseCircleFilled } from "@ant-design/icons"
import { Badge, DatePicker, Input } from "antd"
import { Dayjs } from "dayjs"
import { useCallback, useRef, useState } from "react"
import Icon, { ICON_NAME } from "../../../../../../components/Icon"
import { generateFilterVesselKeys } from "../../../../../../components/Popup/PopupFilterVessel"
import Capabilities from "../../../../../../components/Popup/PopupFilterVessel/Capabilities"
import { datetime } from "../../../../../../util/library/datetime"
import { selectOpMode } from "../../../../../../util/store/selectors"
import { getStore } from "../../../../../../util/store/store"
import { propContainer, SHORT_DATETIME_FORMAT } from "../../../../../../util/varibles/constants"
import { IVessel } from "../../../../../../util/varibles/interface"
import { selectPlan } from "../../../../constants"
import { getTimeByOpMode } from "../../../../util/function_operation/constants"
import { disabledDate } from "../../../Vessels/constants"
import { IMainOp, setSupportOperation } from "./SupportOpContext"
import styles from './style.module.scss'

const VesselFilter: React.FC<{ mainOp: IMainOp['data'] }> = ({ mainOp }) => {
    const set = useRef(setSupportOperation());
    const vessels = useRef(selectPlan.vessels(getStore()).map(item => ({ ...item, opType: generateFilterVesselKeys(item) }) as ({ opType: string[] } & IVessel)));
    const { related_id, id: mainId } = mainOp.operation;
    const [filter, setFilter] = useState<{ [id: string]: boolean }>({ time: false, name: false, capabilities: false });
    const [open, setOpen] = useState(false);

    const handleChangeTime = useCallback((value: [Dayjs | null, Dayjs | null]) => {
        const [startDate, finishDate] = value || [];
        if (startDate && finishDate) {
            setFilter(prev => ({ ...prev, time: true }));
            const start = datetime(startDate).time;
            const finish = datetime(finishDate).time;
            const state = getStore();
            const level = selectPlan.level(state);
            const ops = selectPlan.ops(state);
            const opMode = selectOpMode(state);

            set.current(({ mainOps: prev }) => {
                const { vesselsFilter: old = {} } = prev[mainId] || {};
                return {
                    mainOps: {
                        ...prev,
                        [mainId]: {
                            ...prev[mainId],
                            vesselsFilter: vessels.current.reduce((rs: typeof old, item) => {
                                const { id: vesselId } = item;
                                const isError = Object.keys(level[vesselId] || {}).some((key: string) => {
                                    const { related_id: subRelatedId } = ops[key].operation;
                                    if (related_id === subRelatedId || ops[key].operation.parallel)
                                        return false;
                                    const { activity_log } = ops[key];
                                    const startOp = getTimeByOpMode[opMode](activity_log[0]);
                                    const finishOp = getTimeByOpMode[opMode](activity_log[activity_log.length - 1]);
                                    return start <= finishOp
                                        && startOp <= finish
                                });
                                rs[vesselId] = { ...rs[vesselId] || {}, time: !isError }
                                return rs;
                            }, old)
                        }
                    }
                }
            })
        } else {
            set.current(({ mainOps: prev }) => {
                const { vesselsFilter: old = {} } = prev[mainId] || {};
                return {
                    mainOps: {
                        ...prev,
                        [mainId]: {
                            ...prev[mainId],
                            vesselsFilter: vessels.current.reduce((rs: typeof old, item) => {
                                const { id: vesselId } = item;
                                rs[vesselId] = { ...rs[vesselId] || {}, time: true }
                                return rs;
                            }, old)
                        }
                    }
                }
            })
            setFilter(prev => ({ ...prev, time: false }));
        }
    }, [mainId, related_id])

    const handleSearchName = useCallback((value: string) => {
        const name = value.trim().toLowerCase();
        set.current(({ mainOps: prev }) => {
            const { vesselsFilter: old = {} } = prev[mainId] || {};
            return {
                mainOps: {
                    ...prev,
                    [mainId]: {
                        ...prev[mainId],
                        vesselsFilter: vessels.current.reduce((rs: typeof old, item) => {
                            const { id: vesselId, name: vesselName } = item;
                            rs[vesselId] = { ...rs[vesselId] || {}, name: name.length === 0 || vesselName.toLowerCase().includes(name) }
                            return rs;
                        }, old)
                    }
                }
            }
        })
        setFilter(prev => ({ ...prev, name: name.length > 0 }));
    }, [mainId])

    const handleFilter = useCallback((capabilities: string[]) => {
        const selected = new Set(capabilities)
        set.current(({ mainOps: prev }) => {
            const { vesselsFilter: old = {} } = prev[mainId] || {};
            return {
                mainOps: {
                    ...prev,
                    [mainId]: {
                        ...prev[mainId],
                        vesselsFilter: vessels.current.reduce((rs: typeof old, item) => {
                            const { id: vesselId } = item;
                            rs[vesselId] = { ...rs[vesselId] || {}, capabilities: selected.size === 0 || item.opType.some(type => selected.has(type.toString())) }
                            return rs;
                        }, old)
                    }
                }
            }
        })
        setFilter(prev => ({ ...prev, capabilities: selected.size > 0 }));
    }, [mainId])

    const handleOpen = useCallback(() => {
        setOpen(prev => { return !prev; })
    }, [])

    return <>
        <div className={styles.filter} data-show={open}>
            <div className="min-w-[260px]">
                <DatePicker.RangePicker  {...{
                    ...propContainer,
                    className: 'w-full h-[32px] rounded-[32px]',
                    showTime: { format: 'HH:mm', minuteStep: 15 },
                    disabledDate,
                    format: SHORT_DATETIME_FORMAT,
                    placeholder: ['Start time', 'Finish time'],
                    onChange: handleChangeTime,
                }} />
            </div>
            <Input
                className="h-[32px] min-w-[120px] max-w-[300px] rounded-[20px]"
                placeholder='Search by name'
                onChange={e => handleSearchName(e.target.value)}
                suffix={
                    !!filter.name ? <CloseCircleFilled className="cursor-pointer" onClick={() => handleSearchName("")} /> : <div />
                }
            />
            <Capabilities onChange={capabilities => handleFilter(capabilities)} vessels={vessels.current} />
        </div>
        <div className={["bt-ghost", styles.filterBt].join(' ')} data-icon='true' data-open={open} onClick={handleOpen}>
            <div className="flex items-center gap-1 px-2">
                <Badge className='mt-[3px]' dot={Object.keys(filter).some(key => filter[key])} status="success">
                    <Icon icon={ICON_NAME.FILTER} />
                </Badge>
                <span>Filter</span>
            </div>
        </div>
    </>
}

export default VesselFilter